import React from 'react';
import FlatButton from 'material-ui/FlatButton';
import Dialog from 'material-ui/Dialog';
import mdEntityService, {EntityNameOrSpec} from 'core/metamodel/mdEntityService';
import MdField, {DATATYPE} from 'core/metamodel/mdField';
import Form, {FieldObjectType} from 'core/form/form';
import {createForm, fieldStyles} from 'core/form/createForm';
import StyledSelect from 'core/components/fields/styledSelect';
import {Validate} from 'core/form/validate';
import {ErrorComp} from 'core/components/errorComp/errorComp';
import {MdIconButton} from "core/components/buttons/icons";
import Add from "material-ui/svg-icons/content/add";
import Grid, {GridParams, GridRow} from "core/grid/domain/grid";
import {default as MdEntity, LovItem} from "core/metamodel/mdEntity";
import {action, observable} from "mobx";
import {DomainAncestor, fieldInfo} from "core/common/domain/DomainAncestor";
import commonService from "core/common/service/commonService";
import {FormProps, lovValue, TExtractFieldFn} from "core/form/formUtils";
import {observer} from "mobx-react";
import {DialogAncestor, DialogProps} from "core/common/page/dialogAncestor";
import gridService from "core/grid/gridService";
import {CONDITIONVALUEFORMCONTEXT, FilterFieldComp} from "core/grid/component/conditionValueForm";
import pull = require("lodash/pull");
import SvgDelete = require('react-icons/lib/md/delete');


//@entityConfig()
export class MultiUpdateField extends DomainAncestor {
  @observable fieldName: string;
  @observable fieldValue: any;

  private $entityRef: MdEntity;
  private $fieldRef: MdField;

  constructor (mdEntity: MdEntity, fv:any = "", fn: string = null) {
    super(null);
    this.$entityRef = mdEntity;
    this.fieldValue = fv;
    this.fieldName = fn;
  }

  getMdEntity(): MdEntity {
    return this.$entityRef;
  }

  getMdField(): MdField {
    if (!this.$fieldRef || this.$fieldRef.fieldName !== this.fieldName) {
      this.$fieldRef = this.$entityRef.getField(this.fieldName);
    }
    return this.$fieldRef;
  }
}


//@entityConfig()
export class MultiUpdateDto extends DomainAncestor {
  @observable @fieldInfo(MultiUpdateField) fields: MultiUpdateField[];
  @observable gridParams: GridParams;
  entityName: string;
  $entityRef: MdEntity;
  customName: string;
  count: number;

  constructor(json?: any) {
    super(json);

    if (!this.fields) this.fields = [];

  }

  static newInstance(entityRef: MdEntity, fields: MultiUpdateField[], gridParams: GridParams): MultiUpdateDto {
    let dto = new MultiUpdateDto();
    dto.$entityRef = entityRef;
    dto.entityName = entityRef.id;
    dto.fields = observable.array(fields);
    dto.gridParams = gridParams;
    return dto;
  }


}


const extractFieldFn: TExtractFieldFn<MultiUpdateField> = (dataObject,fieldDef) => {
  let mdField = dataObject.getMdField();
  return (mdField) ? mdField : new MdField({label: 'test', dataType: DATATYPE.STRING});
};


export interface MultiUpdateDialogReturnType {
  saveRequired: boolean;
}

/* ************************************************************************************************
 * ***********   DIALOG  *************************************************************
 * ********************************************************************************************** */


@observer
export class MultiUpdateDialog extends DialogAncestor<DialogProps, undefined, MultiUpdateDialogReturnType> {

  multiUpdateDto: MultiUpdateDto;

  private editGrid: boolean;
  private editFormName: string;
  private grid: Grid;
  private rootObject: DomainAncestor;

  @action open(resolve: any, reject: any, grid: Grid, editFormName: string, rootObject: DomainAncestor) {

    let gridParams = grid.createFullParams();

    console.log('MultiUpdateDialog: Opening dialog for gridParams', gridParams);

    this.resolve = resolve;
    this.reject = reject;
    this.dialogOpen = true;
    this.grid = grid;
    this.rootObject = rootObject;
    this.multiUpdateDto = MultiUpdateDto.newInstance(grid.$entityRef, [new MultiUpdateField(grid.$entityRef)], gridParams);
    this.editFormName = editFormName;
    this.editGrid = (editFormName && (grid.isEditable() || grid.clientGrid));
  }



  _customValidateForm = (field: MultiUpdateField): boolean => {
    this.$errors = [];
    if (!field.fieldName) {
      this.$errors.push({message: "Field name is required"})
    }
    return !this.hasErrors();
  };

  @action ok = () => {
    let res: boolean = true;
    for(let field of this.multiUpdateDto.fields) {
      let form: Form<MultiUpdateField> = field.getForm("MultiUpdateFieldForm");
      res = form.validateForm(field) && res;
      res = this._customValidateForm(field) && res;
    }

    if (res) {

      if (this.editGrid) {
        let selectedRows: GridRow[] = this.grid.getSelectedRows();
        for(let gridRow of selectedRows) {
          let form: Form<any> = gridRow.rowObject.getForm(this.editFormName);
          form.initForm(this.multiUpdateDto.$entityRef, mdEntityService.entityMap, gridRow.rowObject, this.rootObject, null);

          for(let fieldMU of this.multiUpdateDto.fields) {
            form.setValue(this.multiUpdateDto.$entityRef, gridRow.rowObject, form.getField(fieldMU.fieldName), fieldMU.fieldValue);
          }
          gridRow.rowObject.$dirty = true;
          this.rootObject.$dirty = true;
          this.grid.$dirty = true;
        }

        this.dialogOpen = false;
        this.resolve({saveRequired: true});



      } else {
        commonService.loading(true);
        gridService.multiUpdate(this.multiUpdateDto)
          .then((mud: MultiUpdateDto) => {
            commonService.loading(false);
            this.dialogOpen = false;
            this.resolve({saveRequired: false});
            commonService.notifySuccess(`Fields ${this.multiUpdateDto.fields.map(muf => muf.getMdField().gridHeaderLabelActive).join(", ")} were updated on ${mud.count}  ${this.multiUpdateDto.$entityRef.label}`);
          }, error => {
            this.$errors = error.getMessages();
          });
      }

    }
  };

  @action cancel = () => {
    this.dialogOpen = false;
    this.reject("cancel");
  };

  @action addField = (evt) => {
    this.multiUpdateDto.fields.push(new MultiUpdateField(this.multiUpdateDto.$entityRef));
  };



  render() {

    //console.log('MultiUpdateDialog: going to render dialog');

    if (!(this.multiUpdateDto && this.dialogOpen)) return null;

    console.log('MultiUpdateDialog: rendering dialog');

    let fieldOptions: LovItem[];

    if (this.editGrid) {

      let formDefinition = mdEntityService.getFormDefinition(this.editFormName);
      if (!formDefinition) throw new Error("Form Definition for formName " + this.editFormName + " does not exists in FORM_DEFINITION_REGISTRY");
      let form: Form<any> =  new Form(this.editFormName, formDefinition);
      let object = DomainAncestor.getInstance<DomainAncestor>(this.multiUpdateDto.$entityRef.id, null);
      let fields = form.setupFields(object, object, this.multiUpdateDto.$entityRef, mdEntityService.entityMap, null);
      fieldOptions = Object.values(fields).filter((field: FieldObjectType) => field.mdField.multiUpdate)
        .map((field: FieldObjectType) => ({value: field.name, label: field.mdField.label}));
    } else {
      fieldOptions = this.multiUpdateDto.$entityRef.getFields().filter(field => field.multiUpdate)
        .map(field => ({value: field.fieldName, label: field.label}));
    }

    const actions = [
      <FlatButton
        label="Cancel"
        primary
        onClick={this.cancel}
      />,
      <FlatButton
        label="Update"
        secondary
        onClick={this.ok}
      />,
    ];

    return (
      <Dialog
        title={`Update Fields on ${this.grid.getSelectedCount()} ${this.multiUpdateDto.$entityRef.label}`}
        actions={actions}
        modal={true}
        open={this.dialogOpen}
        contentStyle={{width: '60%', maxWidth: 'none'}}
        bodyStyle={{overflow:'visible'}}
      >

        <ErrorComp messages={this.$errors}/>

        <div style={{display: 'flex'}}>
          <div style={{marginRight:10, width: "40%"}}>
            Select Field
          </div>
          <div style={{marginRight:10, width: "50%"}}>
            Fill new value
          </div>
        </div>

        {
          this.multiUpdateDto.fields.map( (field: MultiUpdateField, index) => {
            return <MultiUpdateFieldForm entities={mdEntityService.entityMap} key={index} index={index} dataObject={field}
                                         rootObject={this.multiUpdateDto} fieldOptions={fieldOptions} extractFieldFn={extractFieldFn} />
          } )
        }

        <div className="row">
          <h5 style={{display: 'flex', alignItems: 'center', margin:'0 0 0 5px', fontSize:18}}>
            <span>Add Field</span>
            <MdIconButton onClick={this.addField}>
              <Add />
            </MdIconButton>
          </h5>

        </div>

      </Dialog>
    );
  }

}




/* ************************************************************************************************
 * ***********   FORM  *************************************************************
 * ********************************************************************************************** */


interface MultiUpdateFieldFormProps extends FormProps<MultiUpdateField, MultiUpdateDto> {
  fieldOptions: LovItem[];

}


@createForm({
  formName: 'MultiUpdateFieldForm',
  fields: [{
    name: 'fieldValue',
    setFieldValueFn: (dataObject, value) => dataObject.fieldValue = lovValue(value),
    validators: (mdField) => (mdField.dataType === DATATYPE.NUMBER || mdField.dataType === DATATYPE.NUMBERCURRENCY) ? ['IsNumber'] : [],
    usePlaceholder: true
  }]
}, () => <noscript/>)
@observer
class MultiUpdateFieldForm extends React.Component<MultiUpdateFieldFormProps, undefined> {

  @action onDelete = (evt) => {
    let {dataObject, rootObject} = this.props;
    evt.stopPropagation();
    console.log('onDelete %o', dataObject);
    pull(rootObject.fields, dataObject);
  };

  @action onChangeFieldName = (newField: LovItem) => {

    let {dataObject, rootObject, form} = this.props;

    dataObject.fieldName = lovValue(newField);
    dataObject.fieldValue = "";
    form.updateFieldsPropValue(dataObject, "fieldValue");

    console.log('onChangeFieldName %s', dataObject.fieldName);

    let entitySpec: EntityNameOrSpec[] = (dataObject.getMdField().hasLocalValueSource()) ? [{
        entity: dataObject.getMdField().valueSource,
        lov: true
      }] : [];


    return mdEntityService.fetchEntities(entitySpec)
      .then(action("onChangeFieldName-actionCb", entityMap => {
        form.fields = form.setupFields(dataObject, rootObject, dataObject.getMdEntity(), this.props.entities, (dataObject,fieldDef) => dataObject.getMdField());
      }));

  };

  render() {

    let {dataObject, rootObject, entities, index, form, fieldOptions} = this.props;
    let {fieldName, fieldValue} = form.fields;

    if (fieldValue.props.mdField.dataType === DATATYPE.BOOLEAN) {
      fieldValue.props.onChange = (evt) => {
        let value = lovValue(evt);
        fieldValue.props.value = evt;
        dataObject.fieldValue = JSON.parse(value);
      }
    }

    return (
        <div style={{display: 'flex'}}>
          <div style={{marginRight:10, width: "40%"}}>
            <StyledSelect {...fieldStyles.small.styledSelect} name="fieldColumn" value={dataObject.fieldName} options={fieldOptions} fullWidth={true}
                                                              onChange={this.onChangeFieldName} clearable={false} />
          </div>


          <div style={{marginRight:10, width: "50%"}}>
            { (dataObject.fieldName) ?
            <div>
              <FilterFieldComp {...fieldValue.props} showBooleanField  context={CONDITIONVALUEFORMCONTEXT.MULTIUPDATE}/>
              <Validate field={fieldValue} value={fieldValue.props.value}/>
            </div> : null }
          </div>

          <MdIconButton style={{alignSelf: 'center'}} onClick={this.onDelete} disabled={!index}>
            <SvgDelete size={24}/>
          </MdIconButton>

        </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/core/grid/component/multiUpdateDialog.tsx